import React from "react";
import { Info, Target, Users, Zap, Globe, Rocket } from "lucide-react";
import { motion } from "framer-motion";
import PageBanner from "./PageBanner";

const extraFeatures = [
  { title: "Innovation", description: "Fostering creative problem-solving.", icon: Zap },
  { title: "Global Network", description: "Part of the worldwide IEEE community.", icon: Globe },
  { title: "Career Growth", description: "Accelerating your professional journey.", icon: Rocket },
];

export default function AboutUsPage() {
  return (
    <div className="min-h-screen bg-[#fafcff] flex flex-col">
      <PageBanner 
        title="About Us" 
        description="Discover the passion, the vision, and the community that drives the IEEE ISIMS Student Branch forward." 
        Icon={Info} 
      />

      {/* Main About Content (Tweaked from original) */}
      <div className="relative w-full py-24 overflow-hidden">
        {/* Giant Background Text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.4]">
          <h1 className="text-[12vw] sm:text-[14vw] font-black text-gray-200 whitespace-nowrap tracking-tighter leading-none">
            OUR STORY
          </h1>
        </div>

        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(15, 105, 157, 0.02) 0px, rgba(15, 105, 157, 0.02) 1px, transparent 1px, transparent 18px)`,
          }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 flex flex-col-reverse lg:flex-row gap-16 lg:gap-16 items-center">
          
          {/* Left - Content */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, delay: 0.2 }} 
            viewport={{ once: true }}
            className="lg:w-1/2 space-y-10"
          >
            <div className="space-y-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h2 className="text-[#006699] font-bold tracking-widest uppercase text-xs mb-3 flex items-center gap-2">
                  <span className="w-4 h-[2px] bg-[#006699]"></span> Our Mission
                </h2>
                <h3 className="text-4xl sm:text-5xl font-light text-[#002233] leading-tight">
                  Empowering <br />
                  <span className="text-[#006699] font-bold">Innovators</span>
                </h3>
              </motion.div>
              
              <div className="space-y-4">
                <p className="text-base text-gray-600 leading-relaxed font-medium">
                  IEEE ISIMS SB is a premier student branch of IEEE at the Higher Institute of Computer Science and Multimedia of Sfax. Since our founding, we have been dedicated to advancing technology for humanity.
                </p>
                <p className="text-base text-gray-600 leading-relaxed font-medium">
                  We aim to promote innovation, technical skills, and professional growth through hands-on workshops, grand events, competitive challenges, and endless collaboration opportunities with industry experts.
                </p>
              </div>
            </div>

            <div className="pt-6 border-t-2 border-[#006699]/10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {extraFeatures.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }} 
                    viewport={{ once: true }}
                    className="flex items-start gap-4 group"
                  >
                    <div className="p-2.5 bg-white border border-[#006699]/20 group-hover:bg-[#006699] group-hover:border-[#006699] transition-all duration-300 rounded-none shadow-sm flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#006699] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#00334d] uppercase tracking-wide">{feature.title}</h4>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right - Sharp Creative Visual */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.8, delay: 0.3 }} 
            viewport={{ once: true }}
            className="lg:w-1/2 flex justify-center items-center w-full relative"
          >
            <div className="relative w-full max-w-[450px] aspect-square flex items-center justify-center p-8 bg-white border-4 border-[#006699]/10 shadow-2xl">
              {/* L-Borders */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-[#006699] -mt-1 -ml-1" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-[#006699] -mb-1 -mr-1" />

              <div className="absolute inset-0 bg-[#002233]">
                {/* Texture */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />
                
                {/* Diagonal lines */}
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, #006699 10px, #006699 11px)" }} />
              </div>

              {/* Logo Image */}
              <img 
                src="/logosb.png" 
                alt="IEEE ISIMS SB Logo" 
                className="relative z-10 w-[80%] h-[80%] object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] brightness-0 invert" 
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
